// Dialog di conferma per i conflitti foto ↔ questionario.
// INTERVENTO 3 / passo 3b-2.
//
// Mostrato quando l'analisi è in stato `awaiting_confirmation`. Il
// parrucchiere vede una frase in linguaggio naturale per ogni
// condizione dichiarata dalla cliente che NON è visibile nelle foto.
// Per ciascuna sceglie CONFERMO o NON CONFERMO. Solo dopo aver risposto
// a TUTTE può inviare la risoluzione.

import React, { useMemo, useState } from "react";
import { ShieldCheck, ShieldX, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const CHOICE_ACCEPT = "accept";
const CHOICE_REJECT = "reject";

export default function ConflictConfirmDialog({
  analysisId,
  conflicts,        // [{ tag, natural_text, source_field, source_value }, ...]
  onResolved,       // callback(status) chiamata quando la POST resolve va a buon fine
}) {
  const [choices, setChoices] = useState({});   // { tag: "accept" | "reject" }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const allChosen = useMemo(
    () => conflicts.every((c) => choices[c.tag] === CHOICE_ACCEPT
                              || choices[c.tag] === CHOICE_REJECT),
    [choices, conflicts],
  );

  const setChoice = (tag, value) => {
    setChoices((prev) => ({ ...prev, [tag]: value }));
  };

  const submit = async () => {
    if (!allChosen || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const accepted = conflicts
        .filter((c) => choices[c.tag] === CHOICE_ACCEPT)
        .map((c) => c.tag);
      const rejected = conflicts
        .filter((c) => choices[c.tag] === CHOICE_REJECT)
        .map((c) => c.tag);
      const res = await api.post(
        `/analyses/${analysisId}/resolve-conflicts`,
        { accepted, rejected },
      );
      onResolved?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string"
        ? detail
        : "Impossibile salvare le scelte. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="max-w-2xl mx-auto rounded-2xl border border-[#E5E5E0] bg-white p-8 sm:p-10 animate-fade-up"
      data-testid="conflict-confirm-dialog"
    >
      <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">
        Conferma richiesta
      </p>
      <h2 className="font-serif-display text-2xl sm:text-3xl text-[#1A1A1A] mb-3 leading-tight">
        Alcune dichiarazioni della cliente non sono visibili nelle foto
      </h2>
      <p className="text-sm text-[#737373] mb-7 leading-relaxed">
        Per ciascuna voce, conferma se vuoi tenerne conto nel protocollo
        oppure no. Solo le voci confermate entreranno nella scelta dei prodotti.
      </p>

      <ul className="space-y-5">
        {conflicts.map((c) => {
          const choice = choices[c.tag];
          return (
            <li
              key={c.tag}
              className="rounded-xl border border-[#E5E5E0] p-5 bg-[#FAFAFA]"
              data-testid={`conflict-item-${c.tag}`}
            >
              <p className="text-[15px] text-[#1A1A1A] mb-4 leading-relaxed">
                {c.natural_text}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setChoice(c.tag, CHOICE_ACCEPT)}
                  disabled={submitting}
                  data-testid={`conflict-${c.tag}-accept`}
                  aria-pressed={choice === CHOICE_ACCEPT}
                  className={[
                    "h-10 px-4 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-2",
                    choice === CHOICE_ACCEPT
                      ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                      : "bg-white text-[#1A1A1A] border-[#E5E5E0] hover:border-[#C5A059] hover:text-[#C5A059]",
                  ].join(" ")}
                >
                  <ShieldCheck className="w-4 h-4" />
                  Confermo
                </button>
                <button
                  type="button"
                  onClick={() => setChoice(c.tag, CHOICE_REJECT)}
                  disabled={submitting}
                  data-testid={`conflict-${c.tag}-reject`}
                  aria-pressed={choice === CHOICE_REJECT}
                  className={[
                    "h-10 px-4 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-2",
                    choice === CHOICE_REJECT
                      ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                      : "bg-white text-[#1A1A1A] border-[#E5E5E0] hover:border-[#C5A059] hover:text-[#C5A059]",
                  ].join(" ")}
                >
                  <ShieldX className="w-4 h-4" />
                  Non confermo
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {error && (
        <p className="mt-5 text-sm text-red-700" data-testid="conflict-error">
          {error}
        </p>
      )}

      <div className="mt-8 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={!allChosen || submitting}
          data-testid="conflict-submit"
          className={[
            "h-11 px-6 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors",
            allChosen && !submitting
              ? "bg-[#C5A059] text-white hover:bg-[#B8923E]"
              : "bg-[#E5E5E0] text-[#A3A3A3] cursor-not-allowed",
          ].join(" ")}
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Procedi con il protocollo
        </button>
      </div>

      <p className="mt-6 text-xs text-[#A3A3A3] leading-relaxed">
        Le voci non confermate verranno escluse dalla scelta dei prodotti
        ma resteranno comunque salvate nelle risposte del questionario.
      </p>
    </div>
  );
}
